import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Hidden,
  Typography,
} from "@mui/material";
import {
  AddEcoEfficiencyFormValue,
  EcoEfficiencyContext,
} from "../../../contexts/EcoEfficiencyContext";

import { useAuth } from "../../../contexts/UserContext";
import { CustomAutoComplete } from "../../../components/input/CustomAutoComplete";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import LabelInput from "../../../components/input/LabelInput";
import dayjs, { Dayjs } from "dayjs";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import { OrganizationContext } from "../../../contexts/OrganizationContext";
import { useContext, useEffect, useState } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";

import { EcoEfficiencyService } from "../../../service/eco-efficiency.service";
import type { EcoEfficiencyResponseData } from "../../../models/dto/response/ResponseEcoEfficiency";
import alertService from "../../../components/alert/alertService";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import { useNavigate } from "react-router-dom";
import { MenuData } from "../../../components/input/CustomSelect";
import { common, economic, input } from "../../../i18n";
import { CustomSelect2 } from "../../../components/input/CustomSelect2";
import { isEcoFSubOrgOpen } from "../../../config/config";

export const AddEcoEfficiency = () => {
  const { isAdmin } = useAuth();

  const navigate = useNavigate();

  const { t } = useTranslation([common, input, economic], {
    nsMode: "fallback",
  });

  const {
    modalIsOpen,
    setModalIsOpen,
    baseYearOptions,
    environmentImpactTypeOptions,
    economicIndicators,
    getBaseYear,
    setBaseYearOptions,
    setEcoEfficiency,
    editData,
    setEditData,
    masterEconomicIndicators,
    setSection,
    addEcoEfficiencyForm,
    exitModal,
  } = EcoEfficiencyContext();

  const startDate = addEcoEfficiencyForm.watch("startDate");

  const endDate = addEcoEfficiencyForm.watch("endDate");

  const organizationRef = addEcoEfficiencyForm.watch("organization");

  const suborganizationRef = addEcoEfficiencyForm.watch("suborganizationRef");

  const indicatorRef = addEcoEfficiencyForm.watch("indicatorRef");

  const organizationContext = useContext(OrganizationContext);

  // --- 1. คำนวณ Default Type ตามสิทธิ์ ---
  const defaultTypeValue = !isAdmin() ? "sub-organizations" : "organization";

  // --- 2. ดึงค่า Type และบังคับ Logic ---
  // ถ้าไม่ใช่ Admin ให้ type เป็น sub-organizations เสมอ (เพื่อการแสดงผล UI ที่ถูกต้องทันที)
  // ถ้าเป็น Admin ให้ใช้ค่าจาก Form หรือ Fallback เป็น default
  const watchedType = addEcoEfficiencyForm.watch("type");
  const type = !isAdmin()
    ? "sub-organizations"
    : watchedType ?? defaultTypeValue;

  // --- Logic สำหรับ Type Options ---
  const typeListMenuItem = (() => {
    if (!isAdmin()) {
      return [
        {
          nameEN: t("Sub-Organizations"),
          nameTH: t("Sub-Organizations"),
          value: "sub-organizations",
        },
      ];
    }

    return isEcoFSubOrgOpen
      ? [
          {
            nameEN: t("Organization"),
            nameTH: t("Organization"),
            value: "organization",
          },
          {
            nameEN: t("Sub-Organizations"),
            nameTH: t("Sub-Organizations"),
            value: "sub-organizations",
          },
        ]
      : [
          {
            nameEN: t("Organization"),
            nameTH: t("Organization"),
            value: "organization",
          },
        ];
  })();

  // --- Manage Modal Open/Close & Defaults ---
  useEffect(() => {
    if (modalIsOpen) {
      setModalIsOpen(true);
      buildOptionEconomicIndicators();

      if (!editData) {
        // ตั้งค่า Type เริ่มต้นทันทีเมื่อเปิด Modal สร้างใหม่
        addEcoEfficiencyForm.setValue("type", defaultTypeValue);
      }
    } else {
      // เมื่อปิด Modal ให้ Reset ค่าและคืนค่า Type กลับเป็น Default ตามสิทธิ์
      setEditData(undefined);
      setOptionEconomicIndicators([]);
      addEcoEfficiencyForm.reset({
        type: defaultTypeValue, // สำคัญ: Reset กลับมาเป็นค่าที่ถูกต้อง ไม่ใช่ undefined
      });
    }
  }, [modalIsOpen, editData, defaultTypeValue]);

  const prepareFormData = (data: AddEcoEfficiencyFormValue) => {
    let form: any = {
      startDate: data.startDate.format("YYYY-MM-DD"),
      endDate: data.endDate.format("YYYY-MM-DD"),
      indicatorRef: data.indicatorRef,
      environmentalImpactType: data.environmentalImpactType,
      baseYear: Number(data.baseYear),
    };

    if (data.type === "sub-organizations")
      form.suborganizationRef = data.suborganizationRef;

    if (data.remark !== "" && data.remark) {
      form.remark = data.remark;
    }

    return form;
  };

  const onSubmit = async (data: AddEcoEfficiencyFormValue) => {
    const form = prepareFormData(data);

    if (data.remark === "" && editData) form.remark = "";

    const { startDate, endDate, indicatorRef, ...cleaned } = form;

    const action = editData
      ? EcoEfficiencyService.updateEcoEfficiency(editData._id, cleaned)
      : EcoEfficiencyService.createEcoEfficiency(form);

    let response: EcoEfficiencyResponseData | null = null;
    try {
      response = await action;
    } catch {
      // error alert already shown by service
      return;
    }

    if (response) {
      alertService.mixin(
        t(
          editData
            ? "EcoEfficiency-Edit-Successfully"
            : "EcoEfficiency-Created-Successfully",
        ),
      );

      setEcoEfficiency(response.ecoEfficiencies);

      const item = response.ecoEfficiencies.find(
        (e) =>
          e._id === (editData ? editData._id : response!.ecoefficienciesRef!),
      );

      setEcoEfficiency(response.ecoEfficiencies);

      setEditData(item);

      if (!editData) {
        addEcoEfficiencyForm.reset();
        // Reset type กลับไปเป็นค่า default ตามสิทธิ์หลัง save
        addEcoEfficiencyForm.setValue("type", defaultTypeValue);
        setSection("Add-Actual-Data");
      }
    }
  };

  const scrollToError = (errors: Record<string, any>) => {
    try {
      const errorFields = Object.keys(errors);
      const errorElement = document.getElementById(errorFields[0]);
      errorElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    } catch (error) {
      console.error("Error scrolling to field", error);
    }
  };

  const [optionEconomicIndicators, setOptionEconomicIndicators] = useState<
    MenuData[]
  >([]);

  // ถ้าองค์กรย่อยเปลี่ยน ค่าของ indicator เปลี่ยนด้วย
  useEffect(() => {
    if (
      (organizationRef !== "" || suborganizationRef !== "") &&
      indicatorRef !== "" &&
      indicatorRef !== null &&
      startDate &&
      endDate
    ) {
      const item = masterEconomicIndicators.find((e) => e._id === indicatorRef);

      if (item) {
        if (item.isRequireBaseYear) {
          getBaseYear(
            indicatorRef,
            startDate.month() + 1,
            startDate.year(),
            suborganizationRef !== "" ? suborganizationRef : undefined,
          );
        } else {
          addEcoEfficiencyForm.setValue("baseYear", "");
        }
      }
    }
  }, [suborganizationRef, indicatorRef, startDate]);

  useEffect(() => {
    try {
      if (editData) {
        addEcoEfficiencyForm.setValue("baseYear", `${editData.baseYear}`);
        return;
      }

      if (baseYearOptions.length === 1) {
        addEcoEfficiencyForm.setValue(
          "baseYear",
          baseYearOptions[0].toString(),
        );
      } else if (baseYearOptions.length > 1) {
        addEcoEfficiencyForm.setValue(
          "baseYear",
          baseYearOptions[0].toString(),
        );
      } else {
        setBaseYearOptions([startDate.year()]);
      }
    } catch (error) {}
  }, [baseYearOptions]);

  useEffect(() => {
    const handleNavigation = async () => {
      if (indicatorRef === "add") {
        // 1. แจ้งเตือน User ก่อนเปลี่ยนหน้า
        const isConfirmed = await alertService.confirm(
          t("Add-Economic-Indicator"), // หัวข้อ
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("No-Economic-Indicator-Redirect")}
            </Typography>
          </Box>,
          t("Add-Economic-Indicator"),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false,
          false,
        );

        if (isConfirmed) {
          addEcoEfficiencyForm.setValue("indicatorRef", "", {
            shouldValidate: false,
          });
          navigate("/setting/economic-indicator");
        } else {
          addEcoEfficiencyForm.setValue("indicatorRef", "", {
            shouldValidate: false,
          });
        }
      }
    };

    handleNavigation();
  }, [indicatorRef, navigate, addEcoEfficiencyForm, t]);

  useEffect(() => {
    if (editData) {
      addEcoEfficiencyForm.setValue(
        "suborganizationRef",
        editData.suborganizationRef ? editData.suborganizationRef._id : "",
      );

      addEcoEfficiencyForm.setValue(
        "type",
        editData.suborganizationRef ? "sub-organizations" : "organization",
      );

      addEcoEfficiencyForm.setValue(
        "startDate",
        dayjs(editData.startDate.substring(0, 10)),
      );

      addEcoEfficiencyForm.setValue(
        "endDate",
        dayjs(editData.endDate.substring(0, 10)),
      );

      addEcoEfficiencyForm.setValue(
        "indicatorRef",
        editData.economicindicatorsRef._id,
      );

      addEcoEfficiencyForm.setValue(
        "environmentalImpactType",
        editData.environmentalImpactType,
      );

      addEcoEfficiencyForm.setValue(
        "baseYear",
        editData.baseYear ? editData.baseYear.toString() : "",
      );

      addEcoEfficiencyForm.setValue("remark", editData.remark ?? "");
    } else {
      addEcoEfficiencyForm.reset({
        type: defaultTypeValue, // Reset พร้อมค่า Default
      });
    }
  }, [editData, defaultTypeValue]);

  useEffect(() => {
    addEcoEfficiencyForm.setValue(
      "organizationRef",
      organizationContext?.organization?._id ?? "",
    );
  }, [organizationContext]);

  const buildOptionEconomicIndicators = () => {
    try {
      addEcoEfficiencyForm.clearErrors("indicatorRef");

      let temp: MenuData[] = [];
      // สร้าง option

      if (economicIndicators.length > 0) {
        temp = economicIndicators.map((e) => ({
          nameEN: e.nameEn,
          nameTH: e.nameTh,
          value: e._id,
        }));
      } else if (economicIndicators.length === 0 && isAdmin()) {
        // เพิ่มตัวเลือก Add-Economic-Indicator
        temp.push({
          value: "add",
          nameEN: t(`${"Add-Economic-Indicator"}`),
          nameTH: t(`${"Add-Economic-Indicator"}`),
        });
      } else if (economicIndicators.length === 0 && !isAdmin()) {
        addEcoEfficiencyForm.setError("indicatorRef", {
          message: t("No-Economic-Indicator"),
        });
      }

      setOptionEconomicIndicators(temp);
    } catch (error) {}
  };

  useEffect(() => {
    if (addEcoEfficiencyForm.formState.isSubmitSuccessful) {
      addEcoEfficiencyForm.reset(addEcoEfficiencyForm.getValues(), {
        keepValues: true,
        keepDirty: false,
      });
    }
  }, [addEcoEfficiencyForm.formState.isSubmitSuccessful]);

  useEffect(() => {
    if (optionEconomicIndicators.length === 1 && !editData) {
      addEcoEfficiencyForm.setValue(
        "indicatorRef",
        optionEconomicIndicators[0].value,
      );
    }
  }, [optionEconomicIndicators]);

  return (
    <form
      onSubmit={addEcoEfficiencyForm.handleSubmit(onSubmit, scrollToError)}
      id="form"
    >
      <Grid2 container spacing={"20px"} disableEqualOverflow>
        <Grid2 xs={12}>
          <LabelInput
            label={t("Eco-Efficiency")}
            info={t("Eco-Efficiency-description")}
            required
            showRequired={false}
          />
        </Grid2>

        <Grid2 xs={12} md={6}>
          <CustomSelect2
            name={"type"}
            control={addEcoEfficiencyForm.control}
            label={`${t("Type")}`}
            info={t("Select-Data-Type-Scope")}
            placeholder={t("Select-Param", {
              param: t("Type"),
            })}
            listMenuItem={typeListMenuItem}
            required
            defaultValue={defaultTypeValue}
            rules={{
              onChange(event) {
                addEcoEfficiencyForm.setValue("suborganizationRef", "");
              },
            }}
            // Disable ถ้ากำลัง Edit หรือ ถ้า User ไม่ใช่ Admin (เพราะมีตัวเลือกเดียว)
            disabled={editData ? true : !isAdmin()}
          />
        </Grid2>

        <Grid2
          xs={12}
          md={6}
          container
          alignItems={"end"}
          spacing={"20px"}
          disableEqualOverflow
        >
          <Grid2 xs={12} sm={type === "organization" ? 12 : 6}>
            <InputReactHookForm
              name={"organization"}
              control={addEcoEfficiencyForm.control}
              label={t("Organization")}
              placeholder={""}
              disabled
              // showLabelInput={false}
              defaultValue={organizationContext?.organization?.name ?? ""}
              required
            />
            {/* ✅ แก้ปัญหา uncontrolled input จาก undefined ตรงนี้เลยครับ */}
            <input type="hidden" value={organizationRef ?? ""} />
          </Grid2>

          <Grid2
            xs={12}
            sm={type === "sub-organizations" ? 6 : 0}
            display={type === "sub-organizations" ? "block" : "none"}
          >
            <CustomSelect2
              name={"suborganizationRef"}
              control={addEcoEfficiencyForm.control}
              label={`${t("Sub-Organizations")}`}
              info={t("Sub-Organizations")}
              placeholder={t("Select-Param", {
                param: t("Sub-Organizations"),
              })}
              listMenuItem={
                organizationContext && organizationContext.subOrganizations
                  ? organizationContext?.subOrganizations?.map((sub) => ({
                      nameEN: sub.name,
                      nameTH: sub.name,
                      value: sub.refId,
                    }))
                  : []
              }
              required
              defaultValue={""}
              disabled={editData ? true : false}
            />
          </Grid2>
        </Grid2>

        <Grid2 xs={12} md={6}>
          <FormControl fullWidth>
            <Controller
              name="startDate"
              control={addEcoEfficiencyForm.control}
              defaultValue={dayjs(`${dayjs().year()}-01-01`)}
              rules={{
                required: true,
                onChange: (event) => {
                  const currentValue: Dayjs = event.target.value;
                  addEcoEfficiencyForm.setValue(
                    "endDate",
                    currentValue.add(11, "month"),
                  );
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <CustomDatePicker
                  value={value}
                  label={t("Start-Date")}
                  info={t("Start-Date")}
                  required
                  handleOnChange={onChange}
                  defaultDate={value}
                  inputRef={ref}
                  disableFuture
                  disabled={editData ? true : false}
                />
              )}
            />
          </FormControl>
        </Grid2>

        <Grid2 xs={12} md={6}>
          <FormControl fullWidth>
            <Controller
              name="endDate"
              control={addEcoEfficiencyForm.control}
              defaultValue={dayjs(`${dayjs().year()}-12-01`)}
              rules={{
                onChange(event) {
                  const endDate = dayjs(event.target.value);

                  addEcoEfficiencyForm.setValue(
                    "startDate",
                    endDate.subtract(11, "M"),
                  );
                },
              }}
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <CustomDatePicker
                  value={value}
                  label={t("End-Date")}
                  info={t("End-Date")}
                  handleOnChange={onChange}
                  defaultDate={null}
                  inputRef={ref}
                  error={error}
                  required
                  minDate={startDate}
                  maxDate={dayjs().add(11, "month")}
                  disabled={true}
                />
              )}
            />
          </FormControl>
        </Grid2>

        <Grid2 xs={12} md={6}>
          <CustomAutoComplete
            required
            rules={{
              required: t("This-field-is-required", {
                param: t("Indicator"),
              }),
            }}
            name="indicatorRef"
            control={addEcoEfficiencyForm.control}
            label={t("Indicator")}
            info={t("Economic-Indicators-For-Calculating-Eco-Efficiency")}
            placeholder={t("Select-Param", {
              param: t("Indicator"),
            })}
            listMenuItem={optionEconomicIndicators}
            defaultValue={""}
            disabled={editData ? true : false}
          />
        </Grid2>

        <Grid2 xs={12} md={6}>
          <CustomAutoComplete
            rules={{
              required: t("This-field-is-required", {
                param: t("Base-Year"),
              }),
            }}
            name="baseYear"
            control={addEcoEfficiencyForm.control}
            label={t("Base-Year")}
            info={t("Base-Year-Period-description")}
            placeholder={t("Select-Param", {
              param: t("Base-Year"),
            })}
            listMenuItem={
              baseYearOptions
                ? baseYearOptions.reverse().map((i) => ({
                    nameEN: i.toString(),
                    nameTH: i.toString(),
                    value: i.toString(),
                  }))
                : []
            }
            defaultValue={""}
            required
            disabled={indicatorRef ? false : true}
          />
        </Grid2>

        <Grid2 xs={12} md={6}>
          <CustomAutoComplete
            required
            rules={{
              required: t("This-field-is-required", {
                param: t("Environment-Impact-Type"),
              }),
            }}
            name="environmentalImpactType"
            control={addEcoEfficiencyForm.control}
            label={t("Environment-Impact-Type")}
            info={t("Environment-Impact-Type-Description")}
            placeholder={t("Select-Param", {
              param: t("Environment-Impact-Type"),
            })}
            listMenuItem={
              environmentImpactTypeOptions
                ? environmentImpactTypeOptions.map((i) => ({
                    nameEN: i,
                    nameTH: i,
                    value: i,
                  }))
                : []
            }
            defaultValue={"Emission Scope 1-3"}
          />
        </Grid2>

        <Hidden mdDown>
          <Grid2 xs={6}></Grid2>
        </Hidden>

        <Grid2 xs={12}>
          <InputReactHookForm
            name={"remark"}
            control={addEcoEfficiencyForm.control}
            label={t("Remark")}
            placeholder={t("Remark")}
            info={t("Remark")}
            defaultValue={""}
          />
        </Grid2>

        <Grid2 xs={12}>
          <Box display={"flex"} flexDirection={"row"} gap={"40px"}>
            {editData && (
              <Box flex={1}>
                <Button
                  fullWidth
                  color="secondary"
                  variant="outlined"
                  type="button"
                  name="back"
                  sx={{
                    marginTop: "20px",
                  }}
                  onClick={exitModal}
                >
                  {t("Back")}
                </Button>
              </Box>
            )}

            <Box flex={1}>
              <Button
                fullWidth
                color="secondary"
                variant="contained"
                type="submit"
                name="add"
                sx={{
                  marginTop: "20px",
                }}
                // ✅ Disable ตอนกำลังส่ง
                disabled={addEcoEfficiencyForm.formState.isSubmitting}
                // ✅ เพิ่ม Icon หมุนๆ ตอนกำลังส่ง
                startIcon={
                  addEcoEfficiencyForm.formState.isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {/* เปลี่ยนข้อความตอนโหลดก็ได้ (แล้วแต่ชอบ) */}
                {addEcoEfficiencyForm.formState.isSubmitting
                  ? `${t("Save")}...`
                  : editData
                  ? t("Save")
                  : t("Add")}
              </Button>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
    </form>
  );
};
